import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  UserIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  InformationCircleIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL, getAuthToken } from '../services/api';
import axios from 'axios';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const SendGroupMessagePage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchGroups();
    }
  }, [selectedAgent]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      console.log('Agents API Response:', response.data);
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    if (!selectedAgent) return;

    try {
      setLoadingGroups(true);
      // Find the selected agent from agentOptions to get the webhookApiKey
      const selectedAgentOption = agentOptions.find(option => option.value === selectedAgent);
      if (!selectedAgentOption) {
        console.error('Selected agent option not found');
        return;
      }

      console.log('Fetching groups for client:', selectedAgentOption.clientId, 'Port:', selectedAgentOption.port, 'API Key:', selectedAgentOption.webhookApiKey);
      console.log('Groups endpoint:', `${API_BASE_URL}/api/snwolley/${selectedAgentOption.port}/${selectedAgentOption.clientId}/groups`);

      // Use the /groups endpoint with port, client ID and x-api-key as specified
      const response = await axios.get(`${API_BASE_URL}/api/snwolley/${selectedAgentOption.port}/${selectedAgentOption.clientId}/groups`, {
        headers: {
          'x-api-key': selectedAgentOption.webhookApiKey,
          'Content-Type': 'application/json'
        }
      });

      console.log('Groups API Response:', response.data);
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  // Prepare groups data for SearchableSelect
  const groupOptions = groups && Object.keys(groups).length > 0
    ? Object.values(groups).map((group) => ({
        value: group.id,
        label: group.subject || `Group ${group.id}`,
        description: group.id,
        icon: '👥'
      }))
    : [];

  const handleSendMessage = async () => {
    if (!selectedAgent || !selectedGroup || !message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      console.log('Sending group message:', {
        client_id: client.client_id,
        group_id: selectedGroup,
        message: message
      });

      // Use the new backend API endpoint that handles API key automatically
      const response = await api.sendTextMessage(
        client.client_id,
        selectedGroup,
        message
      );

      console.log('Send message API Response:', response.data);

      toast.success('Group message sent successfully!');
      setMessage('');
      setSelectedGroup('');
    } catch (error) {
      console.error('Error sending group message:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to send group message');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UserGroupIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Send Group Message</h1>
                  <p className="text-sm text-gray-600">Send a text message to a WhatsApp group</p>
                </div>
              </div>
              <button
                onClick={() => {
                  fetchAgents();
                  if (selectedAgent) fetchGroups();
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <PaperAirplaneIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Group Message Details</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to send your group message. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Group Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select Group
              </h3>

              <SearchableSelect
                label="WhatsApp Group"
                name="group"
                options={groupOptions}
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                placeholder={loadingGroups ? "Loading groups..." : "Search and select a group..."}
                isRequired={true}
                isSearchable={true}
                isClearable={true}
                disabled={!selectedAgent || loadingGroups}
              />

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <UserGroupIcon className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-green-800">Group Selection</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Select the WhatsApp group you want to send the message to. Groups are loaded from your selected agent.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Message Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Content <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 resize-none"
                    placeholder="Type your group message here..."
                    required
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-500">
                    Enter the text message you want to send to the group
                  </p>
                  <span className="text-sm text-gray-500">
                    {message.length} characters
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setSelectedGroup('');
                  setMessage('');
                }}
                disabled={sending}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !selectedGroup || !message || sending}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {sending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Sending Message...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Send Group Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendGroupMessagePage;